import DoneAllIcon from '@mui/icons-material/DoneAll';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemButton,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from '@/api/notifications';

/**
 * The bell.
 *
 * TWO QUERIES, NOT ONE, and the split is the point. The badge is polled on an interval; the list
 * is fetched only when the menu opens. Fetching twenty rendered rows every thirty seconds to
 * display one integer is the kind of waste that is invisible on a developer's machine and obvious
 * on a salesperson's phone.
 *
 * The cache is keyed plainly — switching organisation clears the whole query cache (see
 * AuthProvider.resetCache), so the bell cannot carry Acme's approvals under Globex's name for the
 * same reason no other screen can.
 */
export function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: unread = 0 } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 60_000,
    // Cheap, and the first thing someone does after switching windows is look at the bell.
    refetchOnWindowFocus: true,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => fetchNotifications(20),
    // Only while the menu is open. A closed bell has nothing to render.
    enabled: open,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const readOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidate,
  });

  const readAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidate,
  });

  const handleOpen = (item: NotificationItem) => {
    setAnchorEl(null);

    // Marked read on the way out, without waiting for it. Navigation is what the person asked
    // for; the bookkeeping behind it must not make the click feel slow, and a failed mark simply
    // leaves it unread — the safe direction to fail in.
    if (item.read_at === null) readOne.mutate(item.id);
    if (item.url) void navigate(item.url);
  };

  const items = data?.data ?? [];

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          size="small"
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        >
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsNoneIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Notifications
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {unread > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton
                  size="small"
                  onClick={() => readAll.mutate()}
                  disabled={readAll.isPending}
                  aria-label="Mark all as read"
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Notification settings">
              <IconButton
                size="small"
                aria-label="Notification settings"
                onClick={() => {
                  setAnchorEl(null);
                  void navigate('/settings/notifications');
                }}
              >
                <SettingsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider />

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!isLoading && items.length === 0 && (
          <Box sx={{ px: 2, py: 4 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              Nothing waiting on you.
            </Typography>
          </Box>
        )}

        {items.map((item) => (
          <ListItemButton
            key={item.id}
            onClick={() => handleOpen(item)}
            sx={{
              alignItems: 'flex-start',
              gap: 1.5,
              // Unread is marked by a dot rather than a background wash: the list is already
              // sorted unread-first, and two signals for one state is one too many.
              py: 1.25,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                mt: 0.75,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: item.read_at === null ? 'primary.main' : 'transparent',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={item.read_at === null ? 700 : 500}>
                {item.title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', whiteSpace: 'normal' }}
              >
                {item.body}
              </Typography>
            </Box>
          </ListItemButton>
        ))}

        {items.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1 }}>
              <Button
                fullWidth
                size="small"
                onClick={() => {
                  setAnchorEl(null);
                  void navigate('/settings/notifications');
                }}
              >
                Manage what you receive
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
}
